import {
  DEFAULT_REGISTRATION_COUNTRY,
  formatUsPhoneTyping,
  prepopulateMailingFromFirstJurisdiction,
  type RegistrationAccountValues,
  type RegistrationExtendedFormValues,
  type RegistrationJurisdictionRow,
} from "@buxus/shared";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** QC lookup response uses camelCase keys from `CrmContactCrmDto` JSON serialization. */
function strField(obj: Record<string, unknown>, key: string): string {
  const v = obj[key];
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

function stringArrayField(obj: Record<string, unknown>, key: string): string[] {
  const v = obj[key];
  if (!Array.isArray(v)) return [];
  return v
    .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    .map((item) => item.trim());
}

function formatPhoneField(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed ? formatUsPhoneTyping(trimmed) : undefined;
}

/** CRM `Phone`, then `Mobile`, `CellPhone`, `HomePhone`. */
function pickContactPhone(contact: Record<string, unknown>): string {
  return (
    strField(contact, "phone") ||
    strField(contact, "mobile") ||
    strField(contact, "cellPhone") ||
    strField(contact, "homePhone")
  );
}

function yesNoBool(contact: Record<string, unknown>, key: string): boolean | undefined {
  const v = strField(contact, key);
  if (!v) return undefined;
  const lower = v.toLowerCase();
  if (lower === "yes") return true;
  if (lower === "no") return false;
  return undefined;
}

function yesNoNullableBool(contact: Record<string, unknown>, key: string): boolean | null | undefined {
  const v = strField(contact, key);
  if (!v) return undefined;
  const lower = v.toLowerCase();
  if (lower === "yes") return true;
  if (lower === "no") return false;
  if (lower.includes("know")) return null;
  return undefined;
}

function jurisdictionRowsFromLocations(jl: unknown): RegistrationJurisdictionRow[] {
  if (!Array.isArray(jl) || jl.length === 0) return [];
  const rows: RegistrationJurisdictionRow[] = [];
  for (const item of jl) {
    if (!isRecord(item)) continue;
    const country = strField(item, "country") || DEFAULT_REGISTRATION_COUNTRY;
    const state = strField(item, "state");
    const county = strField(item, "name");
    rows.push({ country, state, county });
  }
  return rows;
}

export type QuickCodeLookupParseResult =
  | { ok: true; contact: Record<string, unknown> }
  | { ok: false; message: string; reason: "not_found" | "invalid_response" };

/** POST /api/buxus/get-by-quickcode-computed — GetContactCrmResponse (verify.buxus.io). */
type GetContactCrmResponse = {
  status?: string;
  data?: unknown[];
};

/**
 * Parse GetContactCrmResponse from Quick Code lookup.
 * Empty `jurisdictionLocations` is allowed; jurisdictions are collected on the wizard step.
 */
export function parseQuickCodeLookupResponse(raw: unknown): QuickCodeLookupParseResult {
  if (!isRecord(raw)) {
    return { ok: false, reason: "invalid_response", message: "Invalid response from server." };
  }
  const body = raw as GetContactCrmResponse;
  if (body.status === "NOT_FOUND") {
    return {
      ok: false,
      reason: "not_found",
      message: "Quick Code not found. Check the code and try again.",
    };
  }
  const data = body.data;
  if (!Array.isArray(data) || data.length === 0 || data[0] == null) {
    return {
      ok: false,
      reason: "not_found",
      message: "Quick Code not found. Check the code and try again.",
    };
  }
  const contact = data[0];
  if (!isRecord(contact)) {
    return {
      ok: false,
      reason: "not_found",
      message: "Quick Code not found. Check the code and try again.",
    };
  }
  return { ok: true, contact };
}

/** Map CRM contact row into account + extended defaults (password fields stay empty). */
export function mapQuickCodeContactToForms(contact: Record<string, unknown>): {
  form: Partial<RegistrationAccountValues>;
  extended: Partial<RegistrationExtendedFormValues>;
  quickCodeForCrm: string;
} {
  const communicationsRole = strField(contact, "communicationsRole");
  const firstName = strField(contact, "firstName");
  const lastName = strField(contact, "lastName");
  const email = strField(contact, "email");
  const phoneRaw = pickContactPhone(contact);
  const qc = strField(contact, "quickCodeComputed");
  const jurisdictionRows = jurisdictionRowsFromLocations(contact.jurisdictionLocations);

  // Stakeholder type and job title intentionally omitted — frontend defaults / role logic apply.
  const form: Partial<RegistrationAccountValues> = {
    communicationsRole: communicationsRole || undefined,
    isUnaffiliatedProfessionalErTrainer:
      strField(contact, "isUnaffiliatedProfessionalErTrainer") || undefined,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    phone: phoneRaw ? formatUsPhoneTyping(phoneRaw) : undefined,
    email: email || undefined,
    password: "",
    passwordConfirm: "",
  };

  const extended: Partial<RegistrationExtendedFormValues> = {
    jurisdictions:
      jurisdictionRows.length > 0
        ? jurisdictionRows
        : [{ country: DEFAULT_REGISTRATION_COUNTRY, state: "", county: "" }],
    mailingAddress1: strField(contact, "mailingAddress1"),
    mailingAddress2: strField(contact, "mailingAddress2"),
    mailingCity: strField(contact, "mailingCity"),
    mailingState: strField(contact, "mailingStateNormalized"),
    mailingCounty: strField(contact, "mailingCounty"),
    mailingZip: strField(contact, "mailingZip"),
    mailingCountry: strField(contact, "mailingCountry") || DEFAULT_REGISTRATION_COUNTRY,
    institution: strField(contact, "institution"),
    organizationType: strField(contact, "organizationType"),
    department: strField(contact, "department"),
    institutionPhone: formatPhoneField(strField(contact, "institutionPhone")) ?? "",
    situsAddress1: strField(contact, "situsAddress1"),
    situsAddress2: strField(contact, "situsAddress2"),
    situsCity: strField(contact, "situsCity"),
    situsState: strField(contact, "situsState"),
    situsCounty: strField(contact, "situsCounty"),
    situsZip: strField(contact, "situsZip"),
    situsCountry: strField(contact, "situsCountry") || DEFAULT_REGISTRATION_COUNTRY,
    typeOfAgency: stringArrayField(contact, "typeOfAgency"),
    stationNameOrNumber: strField(contact, "stationNameOrNumber"),
    nameOfAgencyCommandingOfficer: strField(contact, "nameOfAgencyCommandingOfficer"),
    nonEmergencyNumberForAgencyOrDepartment:
      formatPhoneField(strField(contact, "nonEmergencyNumberForAgencyOrDepartment")) ?? "",
    website: strField(contact, "website"),
    staffed: strField(contact, "staffed"),
    numberOfPeopleInDepartment: strField(contact, "numberOfPeopleInDepartment"),
    canYourDepartmentProvide: stringArrayField(contact, "canYourDepartmentProvide"),
    provideMedicalAssistance: yesNoBool(contact, "provideMedicalAssistance"),
    assistInDirectingTraffic: yesNoBool(contact, "assistWithDirectingTraffic"),
    radioCommunication: yesNoBool(contact, "radioCommunicationWithOtherRespondingAgencies"),
    trunkRadios: yesNoBool(contact, "have800TrunkRadios"),
    computerInternetAccess: yesNoBool(contact, "computersOrMobileOnSceneInternetAccess"),
    naturalGasEmergencies: yesNoBool(contact, "naturalGasEmergencies"),
    hazardousLiquidContingencyPlan: yesNoBool(contact, "hazardousLiquidContingencyPlan"),
    publicEvacuation: yesNoBool(contact, "publicEvacuation"),
    hazwoper: strField(contact, "hazwoper"),
    naturalGasNfpa329: yesNoBool(contact, "naturalGasNfpa329"),
    hazardousMaterials: yesNoBool(contact, "hazardousMaterialsWeaponsOfMassDestruction"),
    hazardousLiquids: yesNoBool(contact, "hazardousLiquids"),
    highlyVolatileLiquids: yesNoBool(contact, "highlyVolatileLiquids"),
    pipelineEmergencies: yesNoBool(contact, "pipelineEmergencies"),
    incidentCommand: yesNoBool(contact, "incidentCommand"),
    nationalIncidentManagementSystem: yesNoBool(contact, "nationalIncidentManagementSystem"),
    dotEmergencyResponseGuide: yesNoBool(contact, "dotEmergencyResponseGuide"),
    emergencyMedicalTechnician: yesNoBool(contact, "emergencyMedicalTechnician"),
    mutualAidAgreements: yesNoBool(contact, "mutualAidAgreementsWithAnyAgencies"),
    withWhom: strField(contact, "withWhom"),
    howDidYouFindOutAboutBuxus: strField(contact, "howDidYouFindOutAboutBuxus"),
    howDidYouFindOutAboutBuxusAgency: strField(contact, "howDidYouFindOutAboutBuxusAgency"),
    howDidYouFindOutAboutBuxusAssociation: strField(
      contact,
      "howDidYouFindOutAboutBuxusAssociation",
    ),
    howDidYouFindOutAboutBuxusConference: strField(
      contact,
      "howDidYouFindOutAboutBuxusConference",
    ),
    howDidYouFindOutAboutBuxusOperator: strField(contact, "howDidYouFindOutAboutBuxusOperator"),
    howDidYouFindOutAboutBuxusTrainer: strField(contact, "howDidYouFindOutAboutBuxusTrainer"),
    howDidYouFindOutAboutBuxusWebinar: strField(
      contact,
      "howDidYouFindOutAboutBuxusWebinar",
    ),
    dispatchFacilityClassification: strField(contact, "dispatchFacilityClassification"),
    dispatchDisciplinesServedByAgency: stringArrayField(contact, "dispatchDisciplinesServedByAgency"),
    dispatchMainAreaOfResponsibility: strField(contact, "dispatchMainAreaOfResponsibility"),
    dispatchEstPopulationOfResponsibilityArea: strField(
      contact,
      "dispatchEstPopulationOfResponsibilityArea",
    ),
    dispatchComputerAidedDispatchSystemUsed: strField(
      contact,
      "dispatchComputerAidedDispatchSystemUsed",
    ),
    dispatchComputerAidedDispatchSystemUsedOther: strField(
      contact,
      "dispatchComputerAidedDispatchSystemUsedOther",
    ),
    dispatchCanDetermineCallerLatLongGis: yesNoNullableBool(
      contact,
      "dispatchCanDetermineCallerLatLongGis",
    ),
    dispatchProgramsUsedByAgency: stringArrayField(contact, "dispatchProgramsUsedByAgency"),
    dispatchRadioSystemsUsedByAgency: stringArrayField(contact, "dispatchRadioSystemsUsedByAgency"),
    dispatchRadioSystemsUsedByAgencyOther: strField(
      contact,
      "dispatchRadioSystemsUsedByAgencyOther",
    ),
    dispatchPublicNotificationSystemUsed: strField(contact, "dispatchPublicNotificationSystemUsed"),
    dispatchPublicNotificationSystemUsedOther: strField(
      contact,
      "dispatchPublicNotificationSystemUsedOther",
    ),
    dispatchIsContinuingEducationRequired: strField(contact, "dispatchIsContinuingEducationRequired"),
    dispatchHasAccessToEmergencyTopics: stringArrayField(
      contact,
      "dispatchHasAccessToEmergencyTopics",
    ),
    dispatchSopSogForEmergencyTopics: stringArrayField(contact, "dispatchSopSogForEmergencyTopics"),
    dispatchTrainingCoversEmergencyTopics: stringArrayField(
      contact,
      "dispatchTrainingCoversEmergencyTopics",
    ),
    dispatchCompletedCoursesOnEmergencyTopics: stringArrayField(
      contact,
      "dispatchCompletedCoursesOnEmergencyTopics",
    ),
    dispatchIsRadioSystemEncrypted: yesNoNullableBool(contact, "dispatchIsRadioSystemEncrypted"),
    dispatchIsRadioSystemP25Compliant: yesNoNullableBool(
      contact,
      "dispatchIsRadioSystemP25Compliant",
    ),
    dispatchDoesStateRequireCertification: strField(
      contact,
      "dispatchDoesStateRequireCertification",
    ),
    dispatchDoesStateRequireCertificationOther: strField(
      contact,
      "dispatchDoesStateRequireCertificationOther",
    ),
  };

  return { form, extended, quickCodeForCrm: qc };
}

export function mergeAccountFromQuickCode(
  prev: RegistrationAccountValues,
  patch: Partial<RegistrationAccountValues>,
): RegistrationAccountValues {
  const pick = (next: string | undefined, cur: string) =>
    next !== undefined && next.trim() !== "" ? next.trim() : cur;
  return {
    stakeholderType: pick(patch.stakeholderType, prev.stakeholderType),
    jobTitle: pick(patch.jobTitle, prev.jobTitle),
    isUnaffiliatedProfessionalErTrainer: pick(
      patch.isUnaffiliatedProfessionalErTrainer,
      prev.isUnaffiliatedProfessionalErTrainer,
    ),
    communicationsRole: pick(patch.communicationsRole, prev.communicationsRole),
    firstName: pick(patch.firstName, prev.firstName),
    lastName: pick(patch.lastName, prev.lastName),
    phone: pick(patch.phone, prev.phone),
    email: pick(patch.email, prev.email),
    password: "",
    passwordConfirm: "",
  };
}

export function mergeExtendedFromQuickCode(
  prev: RegistrationExtendedFormValues,
  patch: Partial<RegistrationExtendedFormValues>,
): RegistrationExtendedFormValues {
  const pick = (next: string | undefined, cur: string) =>
    next !== undefined && next.trim() !== "" ? next.trim() : cur;
  const pickBool = (next: boolean | undefined, cur: boolean) =>
    next !== undefined ? next : cur;
  const pickNullableBool = (next: boolean | null | undefined, cur: boolean | null) =>
    next !== undefined && next !== null ? next : cur;
  const mergeJurisdictions = (): RegistrationJurisdictionRow[] => {
    if (!patch.jurisdictions || patch.jurisdictions.length === 0) return prev.jurisdictions;
    return patch.jurisdictions.map((row, i) => ({
      country: pick(row.country, prev.jurisdictions[i]?.country) || DEFAULT_REGISTRATION_COUNTRY,
      state: pick(row.state, prev.jurisdictions[i]?.state),
      county: pick(row.county, prev.jurisdictions[i]?.county),
    }));
  };
  const mergeStringArray = (next: string[] | undefined, cur: string[]): string[] =>
    next && next.length > 0 ? next : cur;

  const merged: RegistrationExtendedFormValues = {
    ...prev,
    jurisdictions: mergeJurisdictions(),
    mailingAddress1: pick(patch.mailingAddress1, prev.mailingAddress1),
    mailingAddress2: pick(patch.mailingAddress2, prev.mailingAddress2),
    mailingCity: pick(patch.mailingCity, prev.mailingCity),
    mailingState: pick(patch.mailingState, prev.mailingState),
    mailingCounty: pick(patch.mailingCounty, prev.mailingCounty),
    mailingZip: pick(patch.mailingZip, prev.mailingZip),
    mailingCountry: pick(patch.mailingCountry, prev.mailingCountry),
    situsAddress1: pick(patch.situsAddress1, prev.situsAddress1),
    situsAddress2: pick(patch.situsAddress2, prev.situsAddress2),
    situsCity: pick(patch.situsCity, prev.situsCity),
    situsState: pick(patch.situsState, prev.situsState),
    situsCounty: pick(patch.situsCounty, prev.situsCounty),
    situsZip: pick(patch.situsZip, prev.situsZip),
    situsCountry: pick(patch.situsCountry, prev.situsCountry),
    institution: pick(patch.institution, prev.institution),
    organizationType: pick(patch.organizationType, prev.organizationType),
    department: pick(patch.department, prev.department),
    institutionPhone: pick(patch.institutionPhone, prev.institutionPhone),
    typeOfAgency: mergeStringArray(patch.typeOfAgency, prev.typeOfAgency),
    stationNameOrNumber: pick(patch.stationNameOrNumber, prev.stationNameOrNumber),
    nameOfAgencyCommandingOfficer: pick(
      patch.nameOfAgencyCommandingOfficer,
      prev.nameOfAgencyCommandingOfficer,
    ),
    nonEmergencyNumberForAgencyOrDepartment: pick(
      patch.nonEmergencyNumberForAgencyOrDepartment,
      prev.nonEmergencyNumberForAgencyOrDepartment,
    ),
    website: pick(patch.website, prev.website),
    staffed: pick(patch.staffed, prev.staffed),
    numberOfPeopleInDepartment: pick(
      patch.numberOfPeopleInDepartment,
      prev.numberOfPeopleInDepartment,
    ),
    canYourDepartmentProvide: mergeStringArray(patch.canYourDepartmentProvide, prev.canYourDepartmentProvide),
    provideMedicalAssistance: pickBool(patch.provideMedicalAssistance, prev.provideMedicalAssistance),
    assistInDirectingTraffic: pickBool(patch.assistInDirectingTraffic, prev.assistInDirectingTraffic),
    radioCommunication: pickBool(patch.radioCommunication, prev.radioCommunication),
    trunkRadios: pickBool(patch.trunkRadios, prev.trunkRadios),
    computerInternetAccess: pickBool(patch.computerInternetAccess, prev.computerInternetAccess),
    naturalGasEmergencies: pickBool(patch.naturalGasEmergencies, prev.naturalGasEmergencies),
    hazardousLiquidContingencyPlan: pickBool(
      patch.hazardousLiquidContingencyPlan,
      prev.hazardousLiquidContingencyPlan,
    ),
    publicEvacuation: pickBool(patch.publicEvacuation, prev.publicEvacuation),
    hazwoper: pick(patch.hazwoper, prev.hazwoper),
    naturalGasNfpa329: pickBool(patch.naturalGasNfpa329, prev.naturalGasNfpa329),
    hazardousMaterials: pickBool(patch.hazardousMaterials, prev.hazardousMaterials),
    hazardousLiquids: pickBool(patch.hazardousLiquids, prev.hazardousLiquids),
    highlyVolatileLiquids: pickBool(patch.highlyVolatileLiquids, prev.highlyVolatileLiquids),
    pipelineEmergencies: pickBool(patch.pipelineEmergencies, prev.pipelineEmergencies),
    incidentCommand: pickBool(patch.incidentCommand, prev.incidentCommand),
    nationalIncidentManagementSystem: pickBool(
      patch.nationalIncidentManagementSystem,
      prev.nationalIncidentManagementSystem,
    ),
    dotEmergencyResponseGuide: pickBool(patch.dotEmergencyResponseGuide, prev.dotEmergencyResponseGuide),
    emergencyMedicalTechnician: pickBool(patch.emergencyMedicalTechnician, prev.emergencyMedicalTechnician),
    mutualAidAgreements: pickBool(patch.mutualAidAgreements, prev.mutualAidAgreements),
    withWhom: pick(patch.withWhom, prev.withWhom),
    howDidYouFindOutAboutBuxus: pick(patch.howDidYouFindOutAboutBuxus, prev.howDidYouFindOutAboutBuxus),
    howDidYouFindOutAboutBuxusAgency: pick(
      patch.howDidYouFindOutAboutBuxusAgency,
      prev.howDidYouFindOutAboutBuxusAgency,
    ),
    howDidYouFindOutAboutBuxusAssociation: pick(
      patch.howDidYouFindOutAboutBuxusAssociation,
      prev.howDidYouFindOutAboutBuxusAssociation,
    ),
    howDidYouFindOutAboutBuxusConference: pick(
      patch.howDidYouFindOutAboutBuxusConference,
      prev.howDidYouFindOutAboutBuxusConference,
    ),
    howDidYouFindOutAboutBuxusOperator: pick(
      patch.howDidYouFindOutAboutBuxusOperator,
      prev.howDidYouFindOutAboutBuxusOperator,
    ),
    howDidYouFindOutAboutBuxusTrainer: pick(
      patch.howDidYouFindOutAboutBuxusTrainer,
      prev.howDidYouFindOutAboutBuxusTrainer,
    ),
    howDidYouFindOutAboutBuxusWebinar: pick(
      patch.howDidYouFindOutAboutBuxusWebinar,
      prev.howDidYouFindOutAboutBuxusWebinar,
    ),
    dispatchFacilityClassification: pick(
      patch.dispatchFacilityClassification,
      prev.dispatchFacilityClassification,
    ),
    dispatchDisciplinesServedByAgency: mergeStringArray(
      patch.dispatchDisciplinesServedByAgency,
      prev.dispatchDisciplinesServedByAgency,
    ),
    dispatchMainAreaOfResponsibility: pick(
      patch.dispatchMainAreaOfResponsibility,
      prev.dispatchMainAreaOfResponsibility,
    ),
    dispatchEstPopulationOfResponsibilityArea: pick(
      patch.dispatchEstPopulationOfResponsibilityArea,
      prev.dispatchEstPopulationOfResponsibilityArea,
    ),
    dispatchComputerAidedDispatchSystemUsed: pick(
      patch.dispatchComputerAidedDispatchSystemUsed,
      prev.dispatchComputerAidedDispatchSystemUsed,
    ),
    dispatchComputerAidedDispatchSystemUsedOther: pick(
      patch.dispatchComputerAidedDispatchSystemUsedOther,
      prev.dispatchComputerAidedDispatchSystemUsedOther,
    ),
    dispatchCanDetermineCallerLatLongGis: pickNullableBool(
      patch.dispatchCanDetermineCallerLatLongGis,
      prev.dispatchCanDetermineCallerLatLongGis,
    ),
    dispatchProgramsUsedByAgency: mergeStringArray(
      patch.dispatchProgramsUsedByAgency,
      prev.dispatchProgramsUsedByAgency,
    ),
    dispatchRadioSystemsUsedByAgency: mergeStringArray(
      patch.dispatchRadioSystemsUsedByAgency,
      prev.dispatchRadioSystemsUsedByAgency,
    ),
    dispatchRadioSystemsUsedByAgencyOther: pick(
      patch.dispatchRadioSystemsUsedByAgencyOther,
      prev.dispatchRadioSystemsUsedByAgencyOther,
    ),
    dispatchPublicNotificationSystemUsed: pick(
      patch.dispatchPublicNotificationSystemUsed,
      prev.dispatchPublicNotificationSystemUsed,
    ),
    dispatchPublicNotificationSystemUsedOther: pick(
      patch.dispatchPublicNotificationSystemUsedOther,
      prev.dispatchPublicNotificationSystemUsedOther,
    ),
    dispatchIsContinuingEducationRequired: pick(
      patch.dispatchIsContinuingEducationRequired,
      prev.dispatchIsContinuingEducationRequired,
    ),
    dispatchHasAccessToEmergencyTopics: mergeStringArray(
      patch.dispatchHasAccessToEmergencyTopics,
      prev.dispatchHasAccessToEmergencyTopics,
    ),
    dispatchSopSogForEmergencyTopics: mergeStringArray(
      patch.dispatchSopSogForEmergencyTopics,
      prev.dispatchSopSogForEmergencyTopics,
    ),
    dispatchTrainingCoversEmergencyTopics: mergeStringArray(
      patch.dispatchTrainingCoversEmergencyTopics,
      prev.dispatchTrainingCoversEmergencyTopics,
    ),
    dispatchCompletedCoursesOnEmergencyTopics: mergeStringArray(
      patch.dispatchCompletedCoursesOnEmergencyTopics,
      prev.dispatchCompletedCoursesOnEmergencyTopics,
    ),
    dispatchIsRadioSystemEncrypted: pickNullableBool(
      patch.dispatchIsRadioSystemEncrypted,
      prev.dispatchIsRadioSystemEncrypted,
    ),
    dispatchIsRadioSystemP25Compliant: pickNullableBool(
      patch.dispatchIsRadioSystemP25Compliant,
      prev.dispatchIsRadioSystemP25Compliant,
    ),
    dispatchDoesStateRequireCertification: pick(
      patch.dispatchDoesStateRequireCertification,
      prev.dispatchDoesStateRequireCertification,
    ),
    dispatchDoesStateRequireCertificationOther: pick(
      patch.dispatchDoesStateRequireCertificationOther,
      prev.dispatchDoesStateRequireCertificationOther,
    ),
  };
  return prepopulateMailingFromFirstJurisdiction(merged);
}
